import React, { useState } from "react";
import { useReveal } from "../hooks/useScroll";

/* SipNScore Control, the thing a client actually logs into. Ten screens in
   three groups, the same shape as the portal's own navigation, so what is
   promised here is what opens. */
const NAV = [
  { g: "Operate", items: [
    { id: "command", t: "Command Centre" },
    { id: "campaigns", t: "Campaigns" },
    { id: "activations", t: "Activations" },
    { id: "team", t: "Team" },
    { id: "scan", t: "Scan Points" },
    { id: "rewards", t: "Rewards" },
  ]},
  { g: "Analyse", items: [
    { id: "insights", t: "Insights" },
    { id: "reports", t: "Client Reports" },
  ]},
  { g: "Configure", items: [
    { id: "demo", t: "Demo Studio" },
    { id: "settings", t: "Settings" },
  ]},
];

const COPY = {
  command: {
    t: "Command Centre",
    p: "The room, live. Scans, responses and claims arriving as they happen, not on a refresh you have to remember to press.",
    pts: [
      ["One subscription, every screen", "Realtime throughout, so a figure on one page can never disagree with the same figure on another."],
      ["Today, at a glance", "What is running, what just happened, and which activation is quietly doing nothing."],
    ],
  },
  campaigns: {
    t: "Campaigns",
    p: "One programme across many places. A national push, a regional test, a single retailer's month, all sharing branding, questions and a reward pool.",
    pts: [
      ["Branding inherits", "A campaign carries the logo, banner and colours. An activation takes them unless it has its own."],
      ["Shared reward pools", "Codes drawn from one pool across locations, with per-activation redemption still attributed correctly."],
    ],
  },
  activations: {
    t: "Activations",
    p: "One place a guest can scan. A bar on a Friday, an aisle for a month, a stand at a festival, a listing that never closes.",
    pts: [
      ["Channel and location type", "Chosen from the taxonomy, not typed, so the rollups mean something later."],
      ["Stop it from your phone", "A stopped activation turns a printed code away the moment the pitch is over."],
    ],
  },
  team: {
    t: "Team",
    p: "Whoever is holding the code. Bartender, promoter, brand ambassador, merchandiser, cashier, field marketer, or a channel with nobody holding anything.",
    pts: [
      ["Seventeen roles", "Including the non-person ones, because on a shelf nobody is standing there."],
      ["Attribution by default", "Every scan carries the person and the place it came from."],
    ],
  },
  scan: {
    t: "Scan Point Studio",
    p: "Design the printed thing. Three card styles, your logo inside the code, the venue and the person on the face, batch download at 2x.",
    pts: [
      ["Your mark in the middle", "SipNScore's by default, or upload the client's."],
      ["Print, screen or table talker", "Same code, whatever it ends up on."],
    ],
  },
  rewards: {
    t: "Rewards",
    p: "Upload the codes, set the limits, and let the pool look after itself. Nothing is assigned until somebody real opens their link.",
    pts: [
      ["Claim links, not codes in email", "A fake address costs nothing, because nothing was ever reserved for it."],
      ["Expiry and recycling", "Unclaimed rewards go back into the pool on a schedule, not by hand."],
    ],
  },
  insights: {
    t: "Insights",
    p: "One model behind everything: funnel, daily trend, hour of day by weekday, per-question breakdowns, sentiment, word clouds, team and channel rollups.",
    pts: [
      ["Day and hour, filtering each other", "Click Friday and the hours redraw for Friday. The chart says how much it is showing."],
      ["Written commentary on request", "Handed finished figures and asked for prose. It is never allowed to do the arithmetic."],
    ],
  },
  reports: {
    t: "Client Reports",
    p: "A live dashboard for a client with no account. Scoped to what you agreed, expiring on a date you set, revocable, view counted.",
    pts: [
      ["One link per location", "Head office sees everything, each store sees only itself, issued in a single action."],
      ["Narrow it, never widen it", "The link is already in somebody's inbox. Showing more means issuing a new one, on the record."],
    ],
  },
  demo: {
    t: "Demo Studio",
    p: "Pitch it in the room. Launch a scenario, put the code on the screen, let people scan it, and watch the numbers move while you talk.",
    pts: [
      ["Present mode", "Full screen, the code sized for the back of the room, counters bumping as answers land."],
      ["Edit it live", "Change a question and it appears on the phone in the client's hand."],
    ],
  },
  settings: {
    t: "Security and settings",
    p: "Mandatory authenticator app on every account, idle sign-out, sign-in throttling, and an append-only audit log nobody can edit.",
    pts: [
      ["Personal data is off by default", "Guests read as references until someone with the right role deliberately reveals them, and that is logged."],
      ["Per-tab access", "Grant a user the screens they need and nothing else. The database rules read the same grant."],
    ],
  },
};

export default function Console() {
  const [id, setId] = useState("command");
  const ref = useReveal();
  const c = COPY[id];

  return (
    <section className="sec sec--pad" id="control" ref={ref} style={{ background: "var(--ink-2)" }}>
      <div className="shell">
        <div className="rv">
          <span className="eyebrow">The console</span>
          <h2 className="h-lg" style={{ marginTop: 20, maxWidth: "16ch" }}>
            Everything the scan becomes.
          </h2>
          <p className="lede" style={{ marginTop: 22 }}>
            SipNScore Control is where the campaign is built, the codes are
            printed, the rewards are held and the numbers are read. Built,
            running and hosted in South Africa.
          </p>
        </div>
      </div>

      <div className="console__tabs">
        {NAV.flatMap((g) => g.items).map((it) => (
          <button
            key={it.id}
            className={it.id === id ? "on" : ""}
            onClick={() => setId(it.id)}
            type="button"
          >
            {it.t}
          </button>
        ))}
      </div>

      <div className="shell" style={{ marginTop: 24 }}>
        <div className="console rv">
          <nav className="console__rail">
            {NAV.map((g) => (
              <React.Fragment key={g.g}>
                <span className="console__grp">{g.g}</span>
                {g.items.map((it) => (
                  <button
                    key={it.id}
                    type="button"
                    className={`console__item${it.id === id ? " on" : ""}`}
                    onClick={() => setId(it.id)}
                  >
                    <i />
                    {it.t}
                  </button>
                ))}
              </React.Fragment>
            ))}
          </nav>
          <div className="console__body">
            <span className="eyebrow">Control</span>
            <h4 style={{ marginTop: 14 }}>{c.t}</h4>
            <p className="body">{c.p}</p>
            <div className="console__pts">
              {c.pts.map(([b, s]) => (
                <div className="console__pt" key={b}>
                  <b>{b}</b>
                  <span>{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
